import React from 'react'
import {
  ExclamationTriangleIcon,
  TruckIcon,
} from '@heroicons/react/24/outline'
import { Loader2 } from 'lucide-react'
import { Button } from '@/shared/ui'
import type { CourierLoadOrder } from '@/shared/api'
import { useCourierLoad } from '@/shared/hooks'
import { OrderStatusBadge } from './OrderStatusBadge'

const DASH = '—'

const formatText = (value: string | null | undefined): string =>
  value && value.trim() ? value : DASH

const formatNumber = (value: number): string =>
  value.toLocaleString('ru-RU')

export interface CourierLoadPanelProps {
  courierId: number | null | undefined
  deliveryDate: number | null | undefined
}

const EmptyNotice: React.FC<{ title: string; text: string }> = ({
  title,
  text,
}) => (
  <section className="rounded-lg border border-gray-200 p-4">
    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
    <p className="mt-1 text-sm text-gray-500">{text}</p>
  </section>
)

const LoadOrderRow: React.FC<{ order: CourierLoadOrder }> = ({ order }) => (
  <li className="space-y-1 py-3 first:pt-0 last:pb-0">
    <div className="flex items-start justify-between gap-3">
      <span className="font-medium text-gray-900">
        № {order.number && order.number.trim() ? order.number : order.shopOrderId}
      </span>
      <OrderStatusBadge status={order.status} statusText={order.statusText} />
    </div>
    <div className="text-xs text-gray-500">
      {formatText(order.deliveryDateFormatted)}
      {order.deliveryInterval?.label
        ? `, ${order.deliveryInterval.label}`
        : ''}
    </div>
    <div className="break-words text-sm text-gray-700">
      {formatText(order.address)}
    </div>
  </li>
)

/**
 * Нагрузка курьера на дату доставки. Только чтение.
 */
export const CourierLoadPanel: React.FC<CourierLoadPanelProps> = ({
  courierId,
  deliveryDate,
}) => {
  const { data, isLoading, isError, refetch } = useCourierLoad(
    courierId ?? null,
    deliveryDate ?? null
  )

  if (!courierId) {
    return (
      <EmptyNotice
        title="Нагрузка курьера"
        text="Не назначен — нагрузка недоступна"
      />
    )
  }

  if (!deliveryDate) {
    return (
      <EmptyNotice
        title="Нагрузка курьера"
        text="Не указана дата доставки — нагрузка не рассчитана"
      />
    )
  }

  if (isLoading) {
    return (
      <section className="rounded-lg border border-gray-200 p-4">
        <div className="flex items-center gap-3 text-sm text-gray-500">
          <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
          Загрузка нагрузки курьера...
        </div>
      </section>
    )
  }

  if (isError || !data) {
    return (
      <section className="rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col items-center py-2 text-center">
          <ExclamationTriangleIcon className="h-8 w-8 text-error-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            Не удалось загрузить нагрузку курьера
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Попробуйте запросить данные ещё раз
          </p>
          <Button
            className="mt-3"
            variant="secondary"
            size="sm"
            onClick={() => void refetch()}
          >
            Повторить
          </Button>
        </div>
      </section>
    )
  }

  return (
    <section className="rounded-lg border border-gray-200 p-4">
      <div className="flex items-center gap-2">
        <TruckIcon className="h-4 w-4 text-gray-400" />
        <h3 className="text-sm font-semibold text-gray-900">
          Нагрузка курьера
        </h3>
      </div>

      <dl className="mt-3 grid grid-cols-2 gap-4">
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Всего заказов
          </dt>
          <dd className="mt-1 text-sm text-gray-900">
            {formatNumber(data.total)}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
            В работе
          </dt>
          <dd className="mt-1 text-sm text-gray-900">
            {formatNumber(data.activeTotal)}
          </dd>
        </div>
      </dl>

      <div className="mt-4 border-t border-gray-100 pt-3">
        <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500">
          Заказы на дату
        </h4>
        {data.orders.length === 0 ? (
          <p className="mt-2 text-sm text-gray-500">
            На эту дату заказов нет
          </p>
        ) : (
          <ul className="mt-2 divide-y divide-gray-100">
            {data.orders.map((order) => (
              <LoadOrderRow key={order.shopOrderId} order={order} />
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}
